import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, X, AlertTriangle } from "lucide-react";

/** value is Array<{name: string, price_per_dozen: number, notice?: string}> */
export default function FlavorPriceEditor({ value = [], onChange, testId = "flavor-price" }) {
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");

  const list = Array.isArray(value) ? value : [];

  const add = () => {
    const cleaned = name.trim();
    if (!cleaned) return;
    if (list.some((f) => (f?.name || "").toLowerCase() === cleaned.toLowerCase())) {
      setName(""); setPrice(""); return;
    }
    const p = parseFloat(price);
    onChange([...list, { name: cleaned, price_per_dozen: isFinite(p) && p >= 0 ? p : 0, notice: "" }]);
    setName(""); setPrice("");
  };

  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const update = (i, patch) =>
    onChange(list.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));

  return (
    <div className="space-y-2">
      {list.length > 0 && (
        <div className="rounded-xl border border-stone-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-xs uppercase tracking-widest text-stone-400">
              <tr>
                <th className="text-left px-3 py-1.5 font-normal">Flavor</th>
                <th className="text-left px-3 py-1.5 font-normal w-28">Price / dz</th>
                <th className="text-left px-3 py-1.5 font-normal">Notice</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((f, i) => (
                // eslint-disable-next-line react/no-array-index-key -- rows are controlled inputs bound to props; renaming would change a name-based key mid-edit and unmount the input.
                <tr key={i} data-testid={`${testId}-row`} className="border-t border-stone-100 align-top">
                  <td className="px-2 py-1.5">
                    <Input
                      value={f.name || ""}
                      onChange={(e) => update(i, { name: e.target.value })}
                      className="h-8 border-none focus-visible:ring-1 focus-visible:ring-blue-300"
                      data-testid={`${testId}-name-${i}`}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm">$</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.5"
                        value={f.price_per_dozen ?? 0}
                        onChange={(e) => update(i, { price_per_dozen: parseFloat(e.target.value) || 0 })}
                        className="h-8 pl-6 border-none focus-visible:ring-1 focus-visible:ring-blue-300"
                        data-testid={`${testId}-price-${i}`}
                      />
                    </div>
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="relative">
                      <AlertTriangle className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-amber-400" />
                      <Input
                        value={f.notice || ""}
                        onChange={(e) => update(i, { notice: e.target.value })}
                        placeholder="e.g. Contains nuts!"
                        className="h-8 pl-7 border-none focus-visible:ring-1 focus-visible:ring-amber-300"
                        data-testid={`${testId}-notice-${i}`}
                      />
                    </div>
                  </td>
                  <td className="text-right px-2 py-1.5">
                    <button
                      type="button"
                      onClick={() => remove(i)}
                      className="text-stone-400 hover:text-red-500 p-1"
                      aria-label="Remove flavor"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex gap-2">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Add a flavor (e.g. Vanilla)"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          data-testid={`${testId}-new-name`}
        />
        <div className="relative w-28 shrink-0">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm">$</span>
          <Input
            type="number"
            min="0"
            step="0.5"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="0"
            className="pl-6"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
            data-testid={`${testId}-new-price`}
          />
        </div>
        <Button
          type="button"
          onClick={add}
          disabled={!name.trim()}
          className="rounded-full bg-blue-900 hover:bg-blue-800 text-white shrink-0"
          data-testid={`${testId}-add-btn`}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      <p className="text-xs text-stone-400">Add a notice (like &quot;contains nuts&quot;, &quot;gluten-free&quot;, or &quot;egg-free&quot;) — customers will see it right on the flavor chip.</p>
    </div>
  );
}
